import React, {
	forwardRef,
	memo,
	useCallback,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';
import * as PropTypes from 'prop-types';
import {
	generateUID,
	isFunction,
	isString,
	isNumber,
	sleep,
	useDebugger,
	useConst,
	isArray,
	isObject,
	useDeepMemoize,
	useControlledState,
	useDeepMemo,
	isNullOrUndefined,
	renderFuncOrElement,
} from '@chkp/mwc-common';
import clsx from 'clsx';
import { commonStyle } from '../commonStyle';
import styles from './DataGrid.scss';

import { Grid } from './Grid';
import { Bars } from './Bars';
import { useArrayStore } from '../store/useArrayStore';

/**
 * Flexible & virtualized data-grid
 */
let DataGrid = (props, ref) => {
	const {
		store: storeProp,
		columns: columnsProp,
		columnDefaults,
		className: classNameProp,
		style,
		tag,
		innerTag,
		domRef,
		domAttributes, //todo: deprecate & use domProps
		rowKey: rowKeyProp = 'uid',
		rowHeight: rowHeightProp = 24,
		estimatedRowHeight = 24,
		rowRenderer: rowRendererProp,
		onRowClick: onRowClickProp,
		onRowDoubleClick: onRowDoubleClickProp,
		onRowMouseDown: onRowMouseDownProp,
		selectedKeys: selectedKeysProp,
		onSelectedKeysChange,
		autoSelectFirst = false,
		selectEvent = 'mousedown',
		onRangeChange: onRangeChangeProp,
		context,
		overscanCount = 10,
		showHeader = true,
		fullSize = true,
		detachedScroll,
		detectHover,
		dragTooltip: dragTooltipProp,
		striped = false,
		outline = false,
		rowHover = true,
		rowBorders = false,
		cellBorders = false,
		rowBackground = true,
		background = true,
		emptyRowContent = false,
		topBar,
		bottomBar,
		leftBar,
		rightBar,
		errorMessage = 'Error',
		emptyMessage = 'No results found',
		loadingMessage = 'Loading...',
		message: messageProp,
		messageMinHeight = 50, // minimal message height for auto-height mode. applies for all message types.
		debug,
		...domProps
	} = props;

	const gridRef = useRef();

	// Apply column-defaults
	const columns = useDeepMemo(
		() =>
			columnDefaults
				? columnsProp.map((col) => ({ ...columnDefaults, ...col }))
				: columnsProp,
		[columnDefaults, columnsProp],
	);

	//-------------------------------------------------------------

	// Automatically create internal array store if store is an array or no store was provided.
	// Rules-of-hooks are preserved by useConst - so it's impossible to switch between manual store and automatic one.
	const isStoreProvided = useConst(storeProp && !isArray(storeProp));

	const store = isStoreProvided
		? storeProp
		: // eslint-disable-next-line react-hooks/rules-of-hooks
		  useArrayStore({
				data: storeProp,
				rowKey: rowKeyProp,
				debug,
		  });

	const {
		getRecordByIndex,
		rowsCount,
		rowKey: storeRowKey,
		setRange: setStoreRange,
		loadRange: loadStoreRange,
		loading,
		error,
	} = store;

	const rowKey = useMemo(() => {
		const _rowKey = storeRowKey || rowKeyProp; // Prefer store's rowKey
		if (isFunction(_rowKey)) return _rowKey;
		if (isString(_rowKey)) return (rec) => rec[_rowKey];
		console.error('invalid rowKey accessor');
		return () => null; // No data. always return null
	}, [rowKeyProp, storeRowKey]);

	const getRowKeyByIdx = useCallback(
		(idx) => {
			const record = getRecordByIndex(idx);
			if (record) {
				const key = rowKey(record, idx);
				if (key == null) {
					console.error(`Missing key for row #${idx}`);
					return `_fallback_${generateUID()}`;
				}
				return key;
			}
			return `_blank_${generateUID()}`; // Row without data. Generate a unique key
		},
		[getRecordByIndex, rowKey],
	);

	//------------------------------------------------------------

	const onRangeChange = useCallback(
		(range) => {
			onRangeChangeProp?.(range);
			setStoreRange?.(range);
		},
		[onRangeChangeProp, setStoreRange],
	);

	//------------------------------------------------------------

	const rowHeight = useMemo(() => {
		if (isNumber(rowHeightProp)) {
			return rowHeightProp;
		}
		if (isFunction(rowHeightProp)) {
			return (idx) => {
				const rec = getRecordByIndex(idx);
				return rec ? rowHeightProp(rec, idx) : estimatedRowHeight;
			};
		}
		return estimatedRowHeight;
	}, [estimatedRowHeight, getRecordByIndex, rowHeightProp]);

	//------------------------------------------------------------
	// Selection

	const [_selectedKeys, setSelectedKeys] = useControlledState(
		selectedKeysProp,
		onSelectedKeysChange,
		[],
	);
	const selectedKeys = useDeepMemoize(_selectedKeys); // optimization: memoize selections by their content
	const shouldAutoSelect = autoSelectFirst && !selectedKeys?.length;

	// Auto-select first row
	useLayoutEffect(() => {
		if (shouldAutoSelect) {
			const firstRec = getRecordByIndex(0);
			const firstUid = firstRec ? rowKey(firstRec) : null;
			firstUid && setSelectedKeys([firstUid]);
		}
	}, [getRecordByIndex, rowKey, setSelectedKeys, shouldAutoSelect]);

	//------------------------------------------------------------

	// Wrappers for row handlers

	const onRowClick = useCallback(
		(idx) => {
			const rec = getRecordByIndex(idx);
			if (selectEvent === 'click') {
				const key = rec ? rowKey(rec) : null;
				setSelectedKeys(key ? [key] : []);
			}

			onRowClickProp?.(rec, idx);
		},
		[getRecordByIndex, rowKey, onRowClickProp, setSelectedKeys, selectEvent],
	);

	const onRowDoubleClick = useCallback(
		(idx) => {
			const rec = getRecordByIndex(idx);
			if (selectEvent === 'doubleclick') {
				const key = rec ? rowKey(rec) : null;
				setSelectedKeys(key ? [key] : []);
			}

			onRowDoubleClickProp?.(rec, idx);
		},
		[
			getRecordByIndex,
			rowKey,
			onRowDoubleClickProp,
			selectEvent,
			setSelectedKeys,
		],
	);

	const onRowMouseDown = useCallback(
		(idx) => {
			const rec = getRecordByIndex(idx);
			if (selectEvent === 'mousedown') {
				const key = rec ? rowKey(rec) : null;
				setSelectedKeys(key ? [key] : []);
			}

			onRowMouseDownProp?.(rec, idx);
		},
		[
			getRecordByIndex,
			rowKey,
			onRowMouseDownProp,
			selectEvent,
			setSelectedKeys,
		],
	);

	//--------------------------------------------------------------

	// Decorate rowContext with additional data - record, selected,..
	const rowRenderer = useCallback(
		(rowContext) => {
			const { index, key } = rowContext;
			const record = getRecordByIndex(index); // can also be retrieved by key. todo: option to retrieve records by key ?
			let selected = false;
			if (record) {
				selected = selectedKeys && selectedKeys.includes(key);
			}
			Object.assign(rowContext, {
				record,
				selected,
				store,
			});
			let mod;
			if (!record && emptyRowContent !== false) {
				mod = emptyRowContent;
			} else {
				mod = rowRendererProp ? rowRendererProp(rowContext) : {};
			}
			if (!isObject(mod)) {
				mod = { content: mod };
			}
			if (mod.content) {
				mod.className = clsx(mod.className, styles.rowWithManualContent);
			}
			return mod;
		},
		[emptyRowContent, getRecordByIndex, rowRendererProp, selectedKeys, store],
	);

	const getRowRange = useCallback(
		(idxRange) => [
			getRecordByIndex(idxRange[0]),
			getRecordByIndex(idxRange[1]),
		],
		[getRecordByIndex],
	);

	//--------------------------------------------------------------
	// Scrollbar dragging

	const onDragStart = useMemo(
		() =>
			detachedScroll
				? (/* idxRange */) => {
						gridRef.current && gridRef.current.detachScroll();
				  }
				: null,
		[detachedScroll],
	);

	const onDragEnd = useMemo(
		() =>
			detachedScroll
				? async (idxRange) => {
						if (gridRef.current) {
							// idxRange may be null if scrollbar wasn't dragged
							if (idxRange) {
								loadStoreRange && (await loadStoreRange(idxRange));
								await sleep(10);
								if (idxRange[1] === rowsCount - 1) {
									gridRef.current.attachScroll(idxRange[1], 'end');
								} else {
									gridRef.current.attachScroll(idxRange[0], 'start');
								}
							} else {
								gridRef.current.attachScroll();
							}
						}
				  }
				: null,
		[detachedScroll, loadStoreRange, rowsCount],
	);

	const dragTooltip = useMemo(
		() =>
			dragTooltipProp
				? (idxRange) => dragTooltipProp(getRowRange(idxRange), idxRange)
				: null,
		[dragTooltipProp, getRowRange],
	);

	//--------------------------------------------------------------

	const message = useMemo(() => {
		if (messageProp) {
			return renderFuncOrElement(messageProp);
		} else if (error) {
			return renderFuncOrElement(errorMessage, error);
		} else if (!rowsCount) {
			if (loading) {
				return renderFuncOrElement(loadingMessage);
			} else {
				return renderFuncOrElement(emptyMessage);
			}
		} else {
			return null;
		}
	}, [
		emptyMessage,
		error,
		errorMessage,
		loading,
		loadingMessage,
		messageProp,
		rowsCount,
	]);

	//--------------------------------------------------------------

	const grid = (
		<Grid
			tag={innerTag}
			ref={gridRef}
			columns={columns}
			message={message}
			rowHeight={rowHeight}
			estimatedRowHeight={estimatedRowHeight}
			getRowKeyByIdx={getRowKeyByIdx}
			rowRenderer={rowRenderer}
			rowsCount={rowsCount}
			onRangeChange={onRangeChange}
			onRowClick={onRowClick}
			onRowDoubleClick={onRowDoubleClick}
			onRowMouseDown={onRowMouseDown}
			context={context}
			overscanCount={overscanCount}
			showHeader={showHeader}
			fullSize={fullSize}
			detachedScroll={detachedScroll}
			detectHover={detectHover}
			dragTooltip={dragTooltip}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			outline={outline}
			rowHover={rowHover}
			rowBorders={rowBorders}
			cellBorders={cellBorders}
			rowBackground={rowBackground}
			striped={striped}
			background={background}
			debug={debug}
			store={store}
			messageMinHeight={messageMinHeight}
		/>
	);

	//--------------------------------------------------------------
	// Resolve bars
	const barData = useMemo(
		() => ({ selectedKeys, setSelectedKeys, store, context }),
		[context, selectedKeys, setSelectedKeys, store],
	);
	//--------------------------------------------------------------

	const getElementDetails = useCallback(
		(el) => {
			const details = gridRef.current?.getElementDetails(el);
			if (!isNullOrUndefined(details?.rowIndex)) {
				// Add record to details
				details.record = getRecordByIndex(details.rowIndex);
			}
			return details;
		},
		[getRecordByIndex],
	);

	//--------------------------------------------------------------
	// Imperative API

	useImperativeHandle(
		ref,
		() => ({
			resetRowHeights: () => gridRef.current.resetRowHeights(),
			getElementDetails,
		}),
		[getElementDetails],
	);

	//--------------------------------------------------------------

	if (debug) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useDebugger('DataGrid', {
			storeProp,
			columnsProp,
			columnDefaults,
			estimatedRowHeight,
			classNameProp,
			style,
			rowKeyProp,
			rowHeightProp,
			rowRendererProp,
			onRowClickProp,
			onRowDoubleClickProp,
			onRowMouseDownProp,
			selectedKeysProp,
			onSelectedKeysChange,
			autoSelectFirst,
			selectEvent,
			onRangeChangeProp,
			context,
			overscanCount,
			showHeader,
			fullSize,
			detachedScroll,
			detectHover,
			dragTooltipProp,
			striped,
			outline,
			rowHover,
			rowBorders,
			cellBorders,
			rowBackground,
			background,
			emptyRowContent,
			//
			columns,
			isStoreProvided,
			store,
			getRecordByIndex,
			rowsCount,
			storeRowKey,
			rowKey,
			setStoreRange,
			loadStoreRange,
			loading,
			error,
			getRowKeyByIdx,
			onRangeChange,
			rowHeight,
			selectedKeys,
			onRowClick,
			onRowDoubleClick,
			onRowMouseDown,
			rowRenderer,
			getRowRange,
			onDragStart,
			onDragEnd,
			dragTooltip,
		});
	}

	//--------------------------------------------------------------

	return (
		<div
			{...domProps}
			{...domAttributes}
			data-tag={tag}
			style={style}
			className={clsx(
				commonStyle,
				styles.datagrid,
				classNameProp,
				fullSize && styles.fullSize,
			)}
			ref={domRef}
		>
			<Bars
				barData={barData}
				topBar={topBar}
				bottomBar={bottomBar}
				leftBar={leftBar}
				rightBar={rightBar}
			>
				{grid}
			</Bars>
		</div>
	);
};

DataGrid = forwardRef(DataGrid);
DataGrid = memo(DataGrid);

DataGrid.propTypes = {
	/**
	 * data-source for the store.
	 * Accepts one of two shapes:
	 * - Array: array of records to display
	 * - Object: api for dynamically loading records. Must provide getRecordByIndex and rowsCount.
	 */
	store: PropTypes.oneOfType([
		PropTypes.array,
		PropTypes.shape({
			/** idx=>record accessor  */
			getRecordByIndex: PropTypes.func.isRequired,
			/** Total available rows. DataGrid will only render & request records of visible rows */
			rowsCount: PropTypes.number.isRequired,
			/** key attribute, or a rec=>key accessor. defaults to 'uid' */
			rowKey: PropTypes.oneOfType([
				PropTypes.string, // string rok key
				PropTypes.func, // rec=>key func
			]),
			/** DataGrid calls setRange([idx0,idx1]) when displayed range is changed (e.g. scroll) */
			setRange: PropTypes.func,

			/** DataGrid awaits for loadRange[idx0,idx1] before releasing scroll on detached mode */
			loadRange: PropTypes.func,

			/**
			 * Indicator that store is loading.
			 * While loading, if rowsCount=0 the grid will display 'loading'. If not loading and rowsCount=0, the grid will display 'No items'
			 */
			loading: PropTypes.bool,

			/**
			 * Indicator that store is in error state.
			 * DataGrid will display 'Error' instead of the data
			 */
			error: PropTypes.object,
		}),
	]),

	/** Columns configuration */
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			/**
			 * unique column key.
			 * record[key] is provided as value arg to column renderer
			 */
			key: PropTypes.string.isRequired,

			/** Label */
			label: PropTypes.string,

			/** static width */
			width: PropTypes.number,

			/**
			 * Relative portion of free space to occupy (after subtracting columns with static width).
			 * Example:
			 *  - Single column with {flex:1} will occupy the full width
			 *  - Two columns flex:2 & flex:3 will occupy 2/5 & 3/5 of the width
			 *
			 * Notes:
			 * -  width takes precedence over flex
			 * -  after user resizes the column, it gets a static width, and flex is ignored
			 * -  Use minWidth & maxWidth to constrain flex columns
			 */
			flex: PropTypes.number,

			/** Min width. takes precedence over width & flex */
			minWidth: PropTypes.number,

			/** Max width. takes precedence over width & flex */
			maxWidth: PropTypes.number,

			/**
			 * Optional cell renderer.
			 * Receives object with the following attributes:
			 *
			 * {
			 *    value,      // Proposed value, retrieved from record[column-key]
			 *    record,     // Record associated with the row
			 *    column,     // Associated column object
			 *    context,    // Arbitrary context which was passed to DataGrid
			 *    isScrolling,// True during scrolling. Use to optimize rendering
			 *    hover,      // True while mouse hovers above the cell. requires 'detectHover' prop to be true.
			 *    padding,    // Custom cell padding. false to disable padding; string to override default padding; otherwise default padding is used ('0 8px')
			 *    row: {      // Row context with the following attributes:
			 *        index,      // Row index
			 *        record,     // Row record (same as cell)
			 *        context,    // Arbitrary DataGrid context (same as cell)
			 *        hover,      // True while mouse hovers above the row. requires 'detectHover' prop to be true.
			 *    }
			 * }
			 *
			 *
			 * May return either a react element (or string), or an object with modifications:
			 *
			 * {
			 *   content,   // Content to render. Similar to returning the element / string directly from the renderer.
			 *   style,     // Styles to apply to the cell. Merged with the built-in style
			 *   className,  // Classname to add to the cell
			 *   wrapper,   // callback that receives the final cell element and can return a modified / wrapped element. Must not add markup!
			 * }
			 *
			 * Notes:
			 * -  By default, DataGrid renders record[column-key] inside the cell
			 * -  Hooks are safe to use within cell renderer, as DataGrid calls it unconditionally and exactly once per render.
			 */
			renderer: PropTypes.func,

			/** Lock column from horizontal scrolling */
			locked: PropTypes.bool,

			/** Allow column resize. Default true */
			resizable: PropTypes.bool,

			/** Set false to disable cell padding, or string to override the default padding (which is '0 8px') */
			padding: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
		}),
	).isRequired,

	/**
	 * Configurations which are applied to all columns.
	 * Useful for DRYing columns configuration, e.g. to disable resize on all columns
	 * Each column may override those it its config.
	 */
	columnDefaults: PropTypes.shape({
		width: PropTypes.number,
		minWidth: PropTypes.number,
		maxWidth: PropTypes.number,
		flex: PropTypes.number,
		resizable: PropTypes.bool,
		padding: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	}),

	/** Wrapper className */
	className: PropTypes.string,

	/**
	 * Wrapper style
	 */
	style: PropTypes.object,

	/** Show columns header. Default true */
	showHeader: PropTypes.bool,

	/**
	 * DataGrid will render itself with width:100% and height:100%.
	 * Defaults to true.
	 *
	 * Note: To control grid's dimension, wrap it in a div rather than passing dimensions manually.
	 *       That's because fullSize=false indicated an auto-height mode, in which DataGrid has to calc its content height.
	 */
	fullSize: PropTypes.bool,

	/** Automation tag */
	tag: PropTypes.string,

	/** Automation tag for the inner table (not including toolbars) */
	innerTag: PropTypes.string,

	/**
	 * key attribute, or a rec=>key accessor. defaults to 'uid'
	 * store.rowKey takes precedence over this one.
	 */
	rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

	/**
	 * Optional row renderer.
	 * Receives object with the following attributes:
	 *
	 * {
	 *    index,      // Row index
	 *    record,     // Record associated with the row
	 *    context,    // Arbitrary context which was passed to DataGrid
	 *    isScrolling, // True during scrolling. Use to optimize rendering
	 *    hover,      // True while mouse hovers above the row. requires 'detectHover' prop to be true.
	 * }
	 *
	 *
	 * May return either:
	 *  - React element (or string), in which case it's rendered *instead* of the cells
	 *  - Modifications object:
	 *
	 *    {
	 *      record,         // Modify the record that is passed to the cells
	 *      content,        // Content to render instead of the cells (similar to returning the content directly from the renderer)
	 *      style,          // Styles to apply to the row. Merged with the built-in style
	 *      className,      // Classname to add to the row
	 *      locked,         // Prevent row contents from being horizontally scrolled (e.g. section titles)
	 *			cellStyle,      // Style to apply to all cells in the row
	 *			cellClassName,  // Classname to add to all cells in the row
	 *			indent,         // Indent width e.g. (for displaying nested tree)
	 *		  isScrolling,    // true during scrolling. use for render optimizations
	 *		  ...any          // Any arbitrary attributes, which can be accessed from cell renderer via {row} arg
	 *    }
	 *
	 * Notes:
	 * -  Hooks are safe to use within row renderer, as DataGrid calls it unconditionally and exactly once per render.
	 *
	 */
	rowRenderer: PropTypes.func,

	/**
	 * Static row height, or a (rec)=>height accessor.
	 * Note: Dynamic height has performance impact on the DataGrid.
	 * Defaults to 24.
	 */
	rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),

	/**  used as row's height in dynamic-height mode, when record is unavailable (e.g. pagination) */
	estimatedRowHeight: PropTypes.number,

	/** Reference to wrapper DOM element */
	domRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.any }),
	]),

	/** Attributes for the wrapper DOM element */
	domAttributes: PropTypes.object, // root element attributes

	/** Called when rendered range changes (at the same point where store.onRangeChange is called) */
	onRangeChange: PropTypes.func,

	/** Arbitrary object which is passed to all renderers and toolbars) */
	context: PropTypes.any,

	/** Extra rows to render outside of visible area */
	overscanCount: PropTypes.number,

	/** Row-click callback. Params: (rec,idx) */
	onRowClick: PropTypes.func,

	/** Row-doubleclick callback. Params: (rec,idx) */
	onRowDoubleClick: PropTypes.func,

	/** Row-mousedown callback. Params: (rec,idx) */
	onRowMouseDown: PropTypes.func,

	/** selected row keys */
	selectedKeys: PropTypes.arrayOf(PropTypes.string),

	/** Callback when selected rows are changed. Params: (String[]) */
	onSelectedKeysChange: PropTypes.func,

	/** Auto-select first row if selectedKeys are empty. */
	autoSelectFirst: PropTypes.bool,

	/** Row event by which DataGrid triggers row selection. Defaults to mousedown */
	selectEvent: PropTypes.oneOf(['click', 'doubleclick', 'mousedown']),

	/** Do not scroll content with vertical scrollbar until it's released and data is loaded (store.loadRange)  */
	detachedScroll: PropTypes.bool,

	/** Detect cell/row hover and pass to renderers as 'hover' prop.
	 * Note: Causes extra re-renders of entered/left rows & cells.
	 * Defaults to false
	 */
	detectHover: PropTypes.bool,

	/**
	 * Display tooltip during vertical scroll. Called with ([first_rec,last_rec],[first_idx,last_idx])
	 * Useful in conjuction with detachedScroll.
	 */
	dragTooltip: PropTypes.func,

	/** Zebra rows */
	striped: PropTypes.bool,

	/** Outline around inner list */
	outline: PropTypes.bool,

	/** Enable automatic row-hover styling. Note: rowRenderer have 'hover' param which can be used for manual styling. */
	rowHover: PropTypes.bool,

	/** Row outlines */
	rowBorders: PropTypes.bool,

	/** Cell outlines */
	cellBorders: PropTypes.bool,

	/** Use default row background (selected rows still get background ) */
	rowBackground: PropTypes.bool,

	/** Use default DataGrid background */
	background: PropTypes.bool,

	/**
	 * Automatically render emptyRowContent in case row-record is not available.
	 * Useful for showing 'Loading' during pagination
	 */
	emptyRowContent: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),

	/** topBar element / renderProp. Gets the following props injected: {context, store, selectedKeys} */
	topBar: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

	/** bottomBar element / renderProp. Gets the following props injected: {context, store, selectedKeys} */
	bottomBar: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

	/** leftBar element / renderProp. Gets the following props injected: {context, store, selectedKeys} */
	leftBar: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

	/** rightBar element / renderProp. Gets the following props injected: {context, store, selectedKeys} */
	rightBar: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

	/** Error message. Either a node, or a render-function receiving an error (provided by the store). Default = "Error" */
	errorMessage: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

	/** Empty message. Either a node, or a render-function. Default="No results found" */
	emptyMessage: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

	/** Loading message. Either a node, or a render-function. Default="Loading..." */
	loadingMessage: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

	/** Custom forced message. Either a node, or a render-function. Takes precedence over error/ empty/ loading messages. */
	message: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

	/** Minimal message height for auto-height mode. applies for all message types. Default 50 */
	messageMinHeight: PropTypes.number,

	debug: PropTypes.bool,
};

export { DataGrid };
